import { Component } from '@theme/component';
import { ThemeEvents, SlideshowSelectEvent, VariantUpdateEvent, VariantSelectedEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.addEventListener('click', this.#handleThumbnailVariantClick, { signal });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles immediate variant selection (pill click) before the fetch completes.
   * Scrolls the gallery to the matching slide right away so there's no delay.
   *
   * @param {VariantSelectedEvent} event
   */
  #handleVariantSelected = (event) => {
    if (!this.slideshow) return;

    const raw = this.dataset.slideVariantOptionIds ?? '';
    if (!raw) return;

    // Find the variant picker and read all currently selected option IDs.
    // updateSelectedOption() has already run before this event fires, so the
    // checked radios already reflect the new combination.
    const section = this.closest('.shopify-section, dialog') ?? document;
    const variantPicker = section.querySelector('variant-picker[data-template-product-match="true"]')
      ?? section.querySelector('variant-picker');
    if (!variantPicker) return;

    const selectedIds = new Set(variantPicker.selectedOptionsValues);

    // Find the slide whose variant option IDs are all present in the current selection.
    const perSlide = raw.split('|');
    const matchIndex = perSlide.findIndex((entry) => {
      const ids = entry.split(',').filter(Boolean);
      return ids.length > 0 && ids.every((id) => selectedIds.has(id));
    });

    if (matchIndex === -1) return;

    const slide = this.slideshow.slides?.[matchIndex];
    if (!slide) return;

    const slideId = slide.getAttribute('slide-id');
    if (slideId) this.slideshow.select({ id: slideId });
  };

  /**
   * Handles a variant update event. When the new variant's featured media
   * matches a slide already rendered in this gallery, animate to it in place.
   * Otherwise (combined listings, hide-unselected-variant-media on, etc.),
   * replace the gallery with the new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    const newVariantMediaId = event.detail.resource?.featured_media?.id;
    const matchingSlide = newVariantMediaId != null
      ? this.querySelector(`slideshow-slide[slide-id="${CSS.escape(String(newVariantMediaId))}"]`)
      : null;

    const currentSlideIds = this.#collectSlideIds(this);
    const newSlideIds = this.#collectSlideIds(newMediaGallery);
    const slideListUnchanged = currentSlideIds.length === newSlideIds.length
      && currentSlideIds.every((id, i) => id === newSlideIds[i]);

    if (matchingSlide && slideListUnchanged && this.slideshow) {
      this.slideshow.select({ id: String(newVariantMediaId) });
      this.dataset.slideVariantOptionIds = newMediaGallery.dataset.slideVariantOptionIds ?? '';
      return;
    }

    this.replaceWith(newMediaGallery);
  };

  /**
   * @param {Element} root
   * @returns {string[]}
   */
  #collectSlideIds(root) {
    return Array.from(root.querySelectorAll('slideshow-slide'))
      .map((slide) => slide.getAttribute('slide-id') ?? '');
  }

  /**
   * Handles a click on a thumbnail that is attached to a variant.
   *
   * @param {MouseEvent} event
   */
  #handleThumbnailVariantClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('button[data-variant-option-ids]');
    if (!(button instanceof HTMLElement)) return;

    this.#selectVariantFromOptionIds(button.dataset.variantOptionIds ?? '');
  };

  /**
   * Handles slideshow slide changes (arrows, drag, scroll). If the newly
   * visible slide is attached to a variant, select it.
   *
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    if (!event.detail?.userInitiated) return;

    const raw = this.dataset.slideVariantOptionIds ?? '';
    if (!raw) return;

    const perSlide = raw.split('|');
    const optionIdsRaw = perSlide[event.detail.index];
    if (!optionIdsRaw) return;

    this.#selectVariantFromOptionIds(optionIdsRaw);
  };

  /**
   * Finds the matching option-value radios in the page's variant-picker and
   * dispatches a change so the existing variant-selection flow runs (URL
   * update, section morph, gallery re-render).
   *
   * @param {string} optionIdsRaw - Comma-separated list of option-value IDs.
   */
  #selectVariantFromOptionIds = (optionIdsRaw) => {
    const optionValueIds = optionIdsRaw.split(',').filter(Boolean);
    if (optionValueIds.length === 0) return;

    const section = this.closest('.shopify-section, dialog') ?? document;
    const variantPicker = section.querySelector('variant-picker[data-template-product-match="true"]')
      ?? section.querySelector('variant-picker');
    if (!variantPicker) return;

    /** @type {HTMLInputElement | null} */
    let lastMatchedInput = null;
    let anyChanged = false;
    for (const optionValueId of optionValueIds) {
      const input = variantPicker.querySelector(
        `input[type="radio"][data-option-value-id="${CSS.escape(optionValueId)}"]`
      );
      if (!(input instanceof HTMLInputElement)) continue;
      if (!input.checked) {
        input.checked = true;
        anyChanged = true;
      }
      lastMatchedInput = input;
    }

    if (!lastMatchedInput || !anyChanged) return;

    lastMatchedInput.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
